
import { useState, useEffect } from "react";
import { dashboardApi } from "@/utils/dashboardAuth";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { clearDashboardSession } from "@/utils/dashboardAuth";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LogOut,
  Search,
  RefreshCw,
  Mail,
  Filter,
  Phone,
  Globe,
  MessageSquare,
  Download,
  Trash2,
  AlertTriangle,
  AlertCircle,
  ExternalLink,
  Copy,
  ChevronLeft,
  ChevronRight,
  Ban,
  X,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface Lead {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  website: string | null;
  message: string | null;
  service: string | null;
  status: string;
  source: string | null;
  is_partial: boolean;
  created_at: string;
}

const LeadsList = ({ onLogout }: { onLogout: () => void }) => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<"active" | "spam" | "all">("active");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState<string | null>(null);
  const [refreshInterval, setRefreshInterval] = useState<number | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { toast } = useToast();

  const selectedIndex = filteredLeads.findIndex((l) => l.id === selectedId);
  const selectedLead = selectedIndex >= 0 ? filteredLeads[selectedIndex] : null;

  const fetchLeads = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await dashboardApi("list");
      const data = res?.leads as any[];
      setLeads(data || []);
      setLastRefreshed(new Date());
    } catch (err: any) {
      console.error("Error in fetchLeads:", err);
      setError(err.message || "Failed to fetch leads");
      toast({
        variant: "destructive",
        title: "Error fetching leads",
        description: err.message || "Failed to fetch leads",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();

    if (autoRefresh && !refreshInterval) {
      const interval = window.setInterval(() => {
        fetchLeads();
      }, 30000);
      setRefreshInterval(interval);
    }

    return () => {
      if (refreshInterval) {
        window.clearInterval(refreshInterval);
      }
    };
  }, [autoRefresh]);

  const handleLogout = () => {
    clearDashboardSession();
    onLogout();
    toast({
      title: "Logged out",
      description: "You have been logged out successfully.",
    });
  };

  const handleRefresh = () => {
    fetchLeads();
    toast({
      title: "Refreshed",
      description: "Leads data has been refreshed.",
    });
  };

  const toggleAutoRefresh = () => {
    const newState = !autoRefresh;
    setAutoRefresh(newState);

    if (newState) {
      toast({
        title: "Auto-refresh enabled",
        description: "Leads will refresh automatically every 30 seconds.",
      });
    } else {
      if (refreshInterval) {
        window.clearInterval(refreshInterval);
        setRefreshInterval(null);
      }
    }
  };

  useEffect(() => {
    let result = [...leads];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (lead) =>
          lead.name?.toLowerCase().includes(term) ||
          lead.email?.toLowerCase().includes(term) ||
          lead.phone?.toLowerCase().includes(term) ||
          lead.website?.toLowerCase().includes(term) ||
          lead.message?.toLowerCase().includes(term) ||
          lead.service?.toLowerCase().includes(term)
      );
    }

    if (filterType) {
      result = result.filter((lead) => lead.source === filterType);
    }

    if (statusFilter === "active") {
      result = result.filter((lead) => lead.status !== "spam");
    } else if (statusFilter === "spam") {
      result = result.filter((lead) => lead.status === "spam");
    }

    result.sort((a, b) => {
      if (a.created_at === null) return 1;
      if (b.created_at === null) return -1;

      if (a.created_at < b.created_at) {
        return sortDirection === "asc" ? -1 : 1;
      }
      if (a.created_at > b.created_at) {
        return sortDirection === "asc" ? 1 : -1;
      }
      return 0;
    });

    setFilteredLeads(result);
  }, [leads, searchTerm, filterType, statusFilter, sortDirection]);

  const goToLead = (index: number) => {
    const next = filteredLeads[index];
    if (next) setSelectedId(next.id);
  };

  // Keyboard navigation: left/right to move through leads, Escape to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" ||
          target.isContentEditable)
      ) {
        return;
      }
      if (!selectedLead) return;
      if (e.key === "ArrowRight") {
        goToLead(selectedIndex + 1);
      } else if (e.key === "ArrowLeft") {
        goToLead(selectedIndex - 1);
      } else if (e.key === "Escape") {
        setSelectedId(null);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [selectedLead, selectedIndex, filteredLeads]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const formatShortDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const sameDay = date.toDateString() === now.toDateString();
    if (sameDay) {
      return new Intl.DateTimeFormat("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    }
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
    }).format(date);
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return `${diffInSeconds} seconds ago`;
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours === 1 ? "" : "s"} ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days === 1 ? "" : "s"} ago`;
    }
  };

  const LeadTypeBadge = ({ type }: { type: string | null }) => {
    switch (type) {
      case "contact":
        return <Badge className="bg-desert-sunset">Contact</Badge>;
      case "newsletter":
        return <Badge className="bg-desert-sky">Newsletter</Badge>;
      case "url_test":
        return <Badge className="bg-desert-cactus">URL Test</Badge>;
      case "contact_partial":
      case "report_scheduler_partial":
        return <Badge variant="outline">Partial</Badge>;
      default:
        return <Badge>{type || "lead"}</Badge>;
    }
  };

  const exportToCSV = () => {
    const csvHeader = [
      "Date",
      "Type",
      "Name",
      "Email",
      "Phone",
      "Website",
      "Message",
      "Service",
      "Status",
    ].join(",");

    const csvRows = filteredLeads.map((lead) => {
      const formattedDate = formatDate(lead.created_at);
      const escapeCsvField = (field: string | null) => {
        if (field === null) return "";
        return `"${String(field).replace(/"/g, '""')}"`;
      };

      return [
        escapeCsvField(formattedDate),
        escapeCsvField(lead.source),
        escapeCsvField(lead.name),
        escapeCsvField(lead.email),
        escapeCsvField(lead.phone),
        escapeCsvField(lead.website),
        escapeCsvField(lead.message),
        escapeCsvField(lead.service),
        escapeCsvField(lead.status),
      ].join(",");
    });

    const csvContent = [csvHeader, ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    const today = new Date();
    const fileName = `leads_export_${today.toISOString().split("T")[0]}.csv`;

    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.display = "none";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "Exported Successfully",
      description: `${filteredLeads.length} leads exported to ${fileName}`,
    });
  };

  const deleteLead = async (id: string) => {
    setDeleteLoading(true);
    try {
      await dashboardApi("delete", { id });

      // Advance to the next lead before removing the current one
      const nextId =
        filteredLeads[selectedIndex + 1]?.id ?? filteredLeads[selectedIndex - 1]?.id ?? null;
      if (selectedId === id) setSelectedId(nextId);

      setLeads((prev) => prev.filter((lead) => lead.id !== id));

      toast({
        title: "Lead Deleted",
        description: "The lead has been permanently removed.",
      });
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Error Deleting Lead",
        description: err.message || "Failed to delete the lead",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const setLeadStatus = async (lead: Lead, status: "spam" | "new") => {
    setStatusLoading(lead.id);
    // Pre-compute where to land: if the lead will leave the current view,
    // advance to the next one so you can sweep through the inbox quickly.
    const idx = filteredLeads.findIndex((l) => l.id === lead.id);
    const nextId = filteredLeads[idx + 1]?.id ?? filteredLeads[idx - 1]?.id ?? null;
    const willBeHidden = status === "spam" ? statusFilter !== "all" : statusFilter === "spam";

    try {
      await dashboardApi("update", { id: lead.id, status });
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, status } : l)));
      if (selectedId === lead.id) {
        setSelectedId(willBeHidden ? nextId : lead.id);
      }
      toast({
        title: status === "spam" ? "Marked as spam" : "Restored from spam",
        description:
          status === "spam"
            ? `${lead.name || "Lead"} marked as spam.`
            : `${lead.name || "Lead"} moved back to your active leads.`,
      });
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Error updating lead",
        description: err.message || "Failed to update the lead",
      });
    } finally {
      setStatusLoading(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold">Leads Dashboard</h1>
        <div className="flex items-center gap-2">
          <Button
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={toggleAutoRefresh}
            className={autoRefresh ? "bg-desert-cactus hover:bg-desert-cactus/90" : ""}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${autoRefresh ? "animate-spin" : ""}`} />
            {autoRefresh ? "Auto-Refresh On" : "Auto-Refresh Off"}
          </Button>
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh Now
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportToCSV}
            disabled={filteredLeads.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="destructive" size="sm" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {!loading && (
        <div className="text-sm text-muted-foreground flex items-center gap-2 mb-2">
          <AlertCircle className="h-4 w-4" />
          Last refreshed: {formatTimeAgo(lastRefreshed)}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-grow">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search leads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select
            value={statusFilter}
            onValueChange={(value) => setStatusFilter(value as "active" | "spam" | "all")}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Show" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="active">Active leads</SelectItem>
                <SelectItem value="spam">Spam only</SelectItem>
                <SelectItem value="all">Everything</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
          <Select
            value={filterType || "all"}
            onValueChange={(value) => setFilterType(value === "all" ? null : value)}
          >
            <SelectTrigger className="w-[170px]">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="contact">Contact Form</SelectItem>
                <SelectItem value="newsletter">Newsletter</SelectItem>
                <SelectItem value="url_test">URL Test</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
          <Select
            value={sortDirection === "desc" ? "newest" : "oldest"}
            onValueChange={(value) => setSortDirection(value === "newest" ? "desc" : "asc")}
          >
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="newest">Newest first</SelectItem>
                <SelectItem value="oldest">Oldest first</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      </div>

      {error && (
        <div className="p-4 mb-4 bg-destructive/10 border border-destructive rounded-md text-destructive">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            <div>
              <p className="font-medium">Error loading leads</p>
              <p>{error}</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            className="mt-2 bg-background"
          >
            Try Again
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-10">
          <RefreshCw className="h-8 w-8 animate-spin text-desert-sunset" />
        </div>
      ) : (
        <div className="grid lg:grid-cols-[360px_1fr] gap-4 items-start">
          {/* ===== Inbox list ===== */}
          <div
            className={cn(
              "glassmorphism dark:glassmorphism-dark rounded-xl border overflow-hidden",
              selectedLead && "hidden lg:block"
            )}
          >
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <span className="text-sm font-medium">
                {filteredLeads.length} {statusFilter === "spam" ? "spam" : "lead"}
                {filteredLeads.length === 1 ? "" : "s"}
              </span>
              <span className="text-xs text-muted-foreground hidden lg:inline">
                Use ← / → keys to move
              </span>
            </div>
            <div className="max-h-[65vh] overflow-y-auto p-2 space-y-1">
              {filteredLeads.length > 0 ? (
                filteredLeads.map((lead) => {
                  const isSpam = lead.status === "spam";
                  return (
                    <button
                      key={lead.id}
                      onClick={() => setSelectedId(lead.id)}
                      className={cn(
                        "w-full text-left px-3 py-2.5 rounded-lg border border-transparent hover:bg-muted/60 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                        selectedId === lead.id && "bg-muted/80 border-border",
                        isSpam && "opacity-60"
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-sm truncate">
                          {lead.name || "Unnamed lead"}
                        </span>
                        <span className="text-xs text-muted-foreground shrink-0">
                          {formatShortDate(lead.created_at)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-0.5">
                        <span className="text-sm text-muted-foreground truncate">
                          {lead.email}
                        </span>
                        {isSpam ? (
                          <Badge
                            variant="outline"
                            className="text-destructive border-destructive/40 shrink-0"
                          >
                            Spam
                          </Badge>
                        ) : (
                          <div className="shrink-0">
                            <LeadTypeBadge type={lead.source} />
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })
              ) : (
                <p className="text-center text-sm text-muted-foreground py-10">
                  No leads found.
                </p>
              )}
            </div>
          </div>

          {/* ===== Detail pane ===== */}
          <div
            className={cn(
              "glassmorphism dark:glassmorphism-dark rounded-xl border lg:sticky lg:top-24",
              !selectedLead && "hidden lg:block"
            )}
          >
            {selectedLead ? (
              <div className="p-5 sm:p-6">
                {/* Navigation header */}
                <div className="flex items-center justify-between gap-2 mb-4">
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      disabled={selectedIndex <= 0}
                      onClick={() => goToLead(selectedIndex - 1)}
                      aria-label="Previous lead"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      disabled={selectedIndex >= filteredLeads.length - 1}
                      onClick={() => goToLead(selectedIndex + 1)}
                      aria-label="Next lead"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground ml-2">
                      {selectedIndex + 1} of {filteredLeads.length}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 lg:hidden"
                    onClick={() => setSelectedId(null)}
                    aria-label="Back to list"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <h2 className="text-xl font-bold flex items-center gap-2 flex-wrap">
                      {selectedLead.name || "Unnamed lead"}
                      {selectedLead.status === "spam" ? (
                        <Badge
                          variant="outline"
                          className="text-destructive border-destructive/40"
                        >
                          Spam
                        </Badge>
                      ) : (
                        <LeadTypeBadge type={selectedLead.source} />
                      )}
                    </h2>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Submitted {formatDate(selectedLead.created_at)}
                    </p>
                  </div>
                </div>

                <div className="space-y-3 mt-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">
                        Email
                      </p>
                      <a
                        href={`mailto:${selectedLead.email}`}
                        className="text-desert-sunset hover:underline break-all"
                      >
                        {selectedLead.email}
                      </a>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      onClick={() => {
                        navigator.clipboard.writeText(selectedLead.email);
                        toast({ title: "Copied", description: "Email copied to clipboard." });
                      }}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>

                  {selectedLead.phone && (
                    <>
                      <Separator />
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <p className="text-xs uppercase tracking-wide text-muted-foreground">
                            Phone
                          </p>
                          <a href={`tel:${selectedLead.phone}`} className="hover:underline">
                            {selectedLead.phone}
                          </a>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0"
                          onClick={() => {
                            navigator.clipboard.writeText(selectedLead.phone!);
                            toast({
                              title: "Copied",
                              description: "Phone number copied to clipboard.",
                            });
                          }}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </>
                  )}

                  {selectedLead.website && (
                    <>
                      <Separator />
                      <div>
                        <p className="text-xs uppercase tracking-wide text-muted-foreground">
                          Website
                        </p>
                        <a
                          href={
                            selectedLead.website.startsWith("http")
                              ? selectedLead.website
                              : `https://${selectedLead.website}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-desert-sunset hover:underline break-all inline-flex items-center gap-1"
                        >
                          {selectedLead.website}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    </>
                  )}

                  {(selectedLead.service || selectedLead.is_partial) && (
                    <>
                      <Separator />
                      <div className="flex flex-wrap gap-x-8 gap-y-2">
                        {selectedLead.service && (
                          <div>
                            <p className="text-xs uppercase tracking-wide text-muted-foreground">
                              Service
                            </p>
                            <p>{selectedLead.service}</p>
                          </div>
                        )}
                        {selectedLead.is_partial && (
                          <div>
                            <p className="text-xs uppercase tracking-wide text-muted-foreground">
                              Status
                            </p>
                            <p>Partial submission</p>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {selectedLead.message && (
                    <>
                      <Separator />
                      <div>
                        <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                          Message
                        </p>
                        <p className="whitespace-pre-wrap text-sm leading-relaxed">
                          {selectedLead.message}
                        </p>
                      </div>
                    </>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-5">
                  <Button asChild size="sm">
                    <a href={`mailto:${selectedLead.email}`}>
                      <Mail className="h-4 w-4 mr-2" />
                      Reply by Email
                    </a>
                  </Button>
                  {selectedLead.phone && (
                    <Button asChild variant="outline" size="sm">
                      <a href={`tel:${selectedLead.phone}`}>
                        <Phone className="h-4 w-4 mr-2" />
                        Call
                      </a>
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const details = [
                        `Name: ${selectedLead.name}`,
                        `Email: ${selectedLead.email}`,
                        selectedLead.phone ? `Phone: ${selectedLead.phone}` : "",
                        selectedLead.website ? `Website: ${selectedLead.website}` : "",
                        selectedLead.service ? `Service: ${selectedLead.service}` : "",
                        selectedLead.message ? `Message: ${selectedLead.message}` : "",
                        `Submitted: ${formatDate(selectedLead.created_at)}`,
                      ]
                        .filter(Boolean)
                        .join("\n");
                      navigator.clipboard.writeText(details);
                      toast({
                        title: "Copied",
                        description: "Full lead details copied to clipboard.",
                      });
                    }}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy All
                  </Button>

                  {selectedLead.status === "spam" ? (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={statusLoading === selectedLead.id}
                      onClick={() => setLeadStatus(selectedLead, "new")}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Not Spam
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive border-destructive/40 hover:bg-destructive/10"
                      disabled={statusLoading === selectedLead.id}
                      onClick={() => setLeadStatus(selectedLead, "spam")}
                    >
                      <Ban className="h-4 w-4 mr-2" />
                      Mark as Spam
                    </Button>
                  )}

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                          <AlertTriangle className="h-5 w-5 text-destructive" />
                          Delete Lead
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete this lead from{" "}
                          {selectedLead.name}? This action cannot be undone and will
                          permanently remove the lead from the database.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => deleteLead(selectedLead.id)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          disabled={deleteLoading}
                        >
                          {deleteLoading ? "Deleting..." : "Delete Lead"}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ) : (
              <div className="p-10 flex flex-col items-center justify-center text-center min-h-[300px]">
                <MessageSquare className="h-10 w-10 text-muted-foreground/40 mb-3" />
                <p className="font-medium">Select a lead to see full details</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Pick one from the list, or use the ← / → keys to move through them.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsList;
